import type {
  BezierPath,
  RawColor,
  VectorBoxParams,
  VectorFillData,
  VectorStrokeData,
} from '@lite-workspace/dto-fotoclass-templates';
import type { RenderVectorBox } from '@lite-workspace/shared/canvas-engine/core';

/** A path command in absolute scene coords; mirrors the Rust `PathSeg` (op ∈ M/L/C/Q/Z). */
export interface ScenePathSeg {
  op: 'M' | 'L' | 'C' | 'Z';
  pts: number[];
}

export type SceneVectorFill =
  | { kind: 'solid'; color: string }
  | { kind: 'linearGradient'; x1: number; y1: number; x2: number; y2: number; stops: { offset: number; color: string }[] }
  | { kind: 'radialGradient'; cx: number; cy: number; r: number; stops: { offset: number; color: string }[] }
  | SceneShapeGradient;

type Bounds = { x: number; y: number; width: number; height: number };
type Affine = [number, number, number, number, number, number];

/** Rust `VectorFill::Gradient` = `GradientOverlayData`: PS ramp + layout, transforms map document px to scene px. */
export interface SceneShapeGradient {
  kind: 'gradient';
  type: string;
  gradient: unknown;
  angle: number;
  scale: number;
  reverse?: boolean;
  align?: boolean;
  offset?: { x: number; y: number };
  dither?: boolean;
  interpolationMethod?: string;
  referenceBounds: Bounds;
  documentBounds: Bounds;
  referenceTransform: Affine;
  documentTransform: Affine;
}

export interface SceneVectorStroke {
  color: string;
  width: number;
  dash: number[];
  lineCap: string;
  lineJoin: string;
  /** PS Inside / Outside on a closed outline; absent = centred. */
  alignment?: 'inside' | 'outside';
  /** Gradient stroke paint (PS, or a legacy linear / radial ramp), laid out as the fill; `color` when absent. */
  fill?: SceneVectorFill;
}

export interface SceneVectorBox {
  type: 'vector';
  id: string;
  rect: { x: number; y: number; width: number; height: number; rotation: number };
  path: ScenePathSeg[];
  fill?: SceneVectorFill;
  stroke?: SceneVectorStroke;
  fillOpacity: number;
  opacity?: number;
  blend: string;
  evenOdd: boolean;
  locked?: boolean;
  /** PS clipping mask: the base this shape is clipped to (`VectorBoxParams.maskBaseBoxId`). */
  clipBaseBoxId?: string;
}

const KAPPA = 0.5522847498307936;

/** Цвет из PSD в строку для движка: это ДАННЫЕ макета, а не цвет интерфейса — токенов у него нет. */
export function rawColorToCss(c: RawColor): string {
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})`;
}

// BezierKnot.points layout (post-normalization, как в VectorRenderer): [cpInY, cpInX, anchorY, anchorX, cpOutY, cpOutX].
const cpInY = (k: { points: number[] }): number => k.points[0];
const cpInX = (k: { points: number[] }): number => k.points[1];
const anchorY = (k: { points: number[] }): number => k.points[2];
const anchorX = (k: { points: number[] }): number => k.points[3];
const cpOutY = (k: { points: number[] }): number => k.points[4];
const cpOutX = (k: { points: number[] }): number => k.points[5];

function bezierPathSegs(paths: readonly BezierPath[], ox: number, oy: number): ScenePathSeg[] {
  const segs: ScenePathSeg[] = [];
  for (const p of paths) {
    if (!p.knots || p.knots.length === 0) continue;
    const k0 = p.knots[0];
    segs.push({ op: 'M', pts: [ox + anchorX(k0), oy + anchorY(k0)] });
    for (let i = 1; i < p.knots.length; i++) {
      const prev = p.knots[i - 1];
      const curr = p.knots[i];
      segs.push({
        op: 'C',
        pts: [ox + cpOutX(prev), oy + cpOutY(prev), ox + cpInX(curr), oy + cpInY(curr), ox + anchorX(curr), oy + anchorY(curr)],
      });
    }

    if (!p.open && p.knots.length >= 2) {
      const last = p.knots[p.knots.length - 1];
      segs.push({
        op: 'C',
        pts: [ox + cpOutX(last), oy + cpOutY(last), ox + cpInX(k0), oy + cpInY(k0), ox + anchorX(k0), oy + anchorY(k0)],
      });
      segs.push({ op: 'Z', pts: [] });
    }
  }

  return segs;
}

function rectSegs(ox: number, oy: number, w: number, h: number): ScenePathSeg[] {
  return [
    { op: 'M', pts: [ox, oy] },
    { op: 'L', pts: [ox + w, oy] },
    { op: 'L', pts: [ox + w, oy + h] },
    { op: 'L', pts: [ox, oy + h] },
    { op: 'Z', pts: [] },
  ];
}

type Radii = readonly [number, number, number, number];

export function roundedRectSegs(ox: number, oy: number, w: number, h: number, radii: Radii): ScenePathSeg[] {
  const m = Math.min(w / 2, h / 2);
  const [tl, tr, br, bl] = radii.map(r => Math.max(0, Math.min(r, m))) as [number, number, number, number];
  const x2 = ox + w;
  const y2 = oy + h;
  const k = KAPPA;
  return [
    { op: 'M', pts: [ox + tl, oy] },
    { op: 'L', pts: [x2 - tr, oy] },
    { op: 'C', pts: [x2 - tr + tr * k, oy, x2, oy + tr - tr * k, x2, oy + tr] },
    { op: 'L', pts: [x2, y2 - br] },
    { op: 'C', pts: [x2, y2 - br + br * k, x2 - br + br * k, y2, x2 - br, y2] },
    { op: 'L', pts: [ox + bl, y2] },
    { op: 'C', pts: [ox + bl - bl * k, y2, ox, y2 - bl + bl * k, ox, y2 - bl] },
    { op: 'L', pts: [ox, oy + tl] },
    { op: 'C', pts: [ox, oy + tl - tl * k, ox + tl - tl * k, oy, ox + tl, oy] },
    { op: 'Z', pts: [] },
  ];
}

export function ellipseSegs(ox: number, oy: number, w: number, h: number): ScenePathSeg[] {
  const cx = ox + w / 2;
  const cy = oy + h / 2;
  const rx = w / 2;
  const ry = h / 2;
  const k = KAPPA;
  return [
    { op: 'M', pts: [cx + rx, cy] },
    { op: 'C', pts: [cx + rx, cy + ry * k, cx + rx * k, cy + ry, cx, cy + ry] },
    { op: 'C', pts: [cx - rx * k, cy + ry, cx - rx, cy + ry * k, cx - rx, cy] },
    { op: 'C', pts: [cx - rx, cy - ry * k, cx - rx * k, cy - ry, cx, cy - ry] },
    { op: 'C', pts: [cx + rx * k, cy - ry, cx + rx, cy - ry * k, cx + rx, cy] },
    { op: 'Z', pts: [] },
  ];
}

// Twin of Rust `outline_in_frame`: the frame is the PS pixel bounds (outline + antialiasing pad + stroke), the exact
// outline rides frame-local in `paths`; trusted only inside the frame (±0.5 px) spanning ≥ half of it per axis.
// The stroke counts toward the span: a 1 px bar with a 16 px stroke has a 19 px frame, and its outline is the real one.
function outlineInFrame(paths: readonly BezierPath[], w: number, h: number, stroke: VectorStrokeData | undefined): boolean {
  const reach = strokeReach(stroke);
  let [x0, y0, x1, y1] = [Infinity, Infinity, -Infinity, -Infinity];
  for (const k of paths.flatMap(p => p.knots)) {
    for (let i = 0; i + 1 < k.points.length; i += 2) {
      [y0, y1] = [Math.min(y0, k.points[i]), Math.max(y1, k.points[i])];
      [x0, x1] = [Math.min(x0, k.points[i + 1]), Math.max(x1, k.points[i + 1])];
    }
  }

  const slack = 0.5;
  return (
    x0 >= -slack &&
    y0 >= -slack &&
    x1 <= w + slack &&
    y1 <= h + slack &&
    x1 - x0 + 2 * reach >= w / 2 &&
    y1 - y0 + 2 * reach >= h / 2
  );
}

/** How far the stroke reaches past the outline: inside 0, outside its width, centred half of it; a width below 0 or NaN 0. */
export function strokeReach(stroke: VectorStrokeData | undefined): number {
  if (!stroke || stroke.alignment === 'inside') return 0;
  const width = stroke.width > 0 ? stroke.width : 0;
  return stroke.alignment === 'outside' ? width : width / 2;
}

function buildPath(vector: VectorBoxParams, ox: number, oy: number, w: number, h: number): ScenePathSeg[] {
  const o = vector.origination;
  const paths = vector.paths ?? [];
  const outline =
    paths.length > 0 && paths.every(p => !p.open) && outlineInFrame(paths, w, h, vector.stroke)
      ? bezierPathSegs(paths, ox, oy)
      : undefined;
  switch (o?.kind) {
    case 'rectangle':
      return outline ?? rectSegs(ox, oy, w, h);
    case 'roundedRect':
      return outline ?? roundedRectSegs(ox, oy, w, h, o.radii);
    case 'ellipse':
      return outline ?? ellipseSegs(ox, oy, w, h);
    case 'line': {
      // Кадр у `from`/`to` страничный: парсер строит их диагональю `keyOriginShapeBBox` в
      // координатах документа, там же, где `rect`. Прибавить к ним начало рамки — перенести
      // фигуру второй раз; та же геометрия лежит рядом, в `paths`, и уже нормирована на рамку.
      const segs = bezierPathSegs(vector.paths ?? [], ox, oy);
      if (segs.length > 0) return segs;
      // Контура может не быть вовсе: `case 4` парсера не требует `vectorMask`. Тогда остаётся
      // документная копия — её кладём как есть, без начала рамки.
      return [
        { op: 'M', pts: [o.from.x, o.from.y] },
        { op: 'L', pts: [o.to.x, o.to.y] },
      ];
    }

    case 'path':
      return bezierPathSegs(vector.paths ?? [], ox, oy);
    default:
      return []; // malformed/raster-only vector → caller falls back to a rasterized preview
  }
}

// The gradient axis through the box center at `angle`, in box-local px.
// Direction: PS measures the angle in a y-up frame; the canvas/scene is y-down, so the sin component
// is reflected (negated angle) — the same math→screen Y-flip the shadow extractor documents.
// Length: the axis is CLIPPED to the bbox boundary along the direction (libpsd/Krita tan-geometry),
// NOT the bbox projection — the cross dimension does not participate. PS Scale% then stretches the
// half-span about the center by (1 + scale)/2 — both calibrated against a real PS composite; the
// full law lives in libs/shared/ps-effects/README.md («Осевая геометрия»).
function gradientEndpoints(
  angleDeg: number,
  w: number,
  h: number,
  scale = 1,
): { x1: number; y1: number; x2: number; y2: number } {
  const a = (-angleDeg * Math.PI) / 180;
  const ux = Math.cos(a);
  const uy = Math.sin(a);
  const halfW = Math.abs(ux) < 1e-6 ? Infinity : w / (2 * Math.abs(ux));
  const halfH = Math.abs(uy) < 1e-6 ? Infinity : h / (2 * Math.abs(uy));
  const half = Math.min(halfW, halfH) * ((1 + scale) / 2);
  const cx = w / 2;
  const cy = h / 2;
  return { x1: cx - ux * half, y1: cy - uy * half, x2: cx + ux * half, y2: cy + uy * half };
}

export function buildFill(
  fill: VectorFillData | undefined,
  ox: number,
  oy: number,
  w: number,
  h: number,
): SceneVectorFill | undefined {
  if (!fill) return undefined;
  switch (fill.kind) {
    case 'solid':
      return { kind: 'solid', color: rawColorToCss(fill.color) };
    case 'linearGradient': {
      const e = gradientEndpoints(fill.angle, w, h, fill.scale ?? 1);
      return {
        kind: 'linearGradient',
        x1: ox + e.x1,
        y1: oy + e.y1,
        x2: ox + e.x2,
        y2: oy + e.y2,
        stops: fill.stops.map(s => ({ offset: s.offset, color: rawColorToCss(s.color) })),
      };
    }

    case 'radialGradient':
      return {
        kind: 'radialGradient',
        cx: ox + fill.center.x,
        cy: oy + fill.center.y,
        r: fill.radius,
        stops: fill.stops.map(s => ({ offset: s.offset, color: rawColorToCss(s.color) })),
      };
    case 'pattern':
      return undefined; // pattern fill not supported in the overlay (Konva also falls back to transparent)
    case 'gradient':
      return undefined; // laid out in the drawn outline's bounds: `shapeGradientFill`
  }
}

// Twin of Rust `shape_gradient_fill`: PS frames a shape's gradient fill by its path's whole-pixel control bounds,
// clipped to the canvas, or by the canvas with Align off. `path`: page px; `ox/oy`: the page's document origin.
function shapeGradientFill(
  fill: Extract<VectorFillData, { kind: 'gradient' }>,
  path: readonly ScenePathSeg[],
  ox: number,
  oy: number,
): SceneShapeGradient {
  let [x0, y0, x1, y1] = [Infinity, Infinity, -Infinity, -Infinity];
  for (const seg of path) {
    for (let i = 0; i + 1 < seg.pts.length; i += 2) {
      [x0, x1] = [Math.min(x0, seg.pts[i] - ox), Math.max(x1, seg.pts[i] - ox)];
      [y0, y1] = [Math.min(y0, seg.pts[i + 1] - oy), Math.max(y1, seg.pts[i + 1] - oy)];
    }
  }

  const d = fill.documentBounds;
  [x0, y0] = [Math.max(Math.floor(x0), d.x), Math.max(Math.floor(y0), d.y)];
  [x1, y1] = [Math.min(Math.ceil(x1), d.x + d.width), Math.min(Math.ceil(y1), d.y + d.height)];
  const place: Affine = [1, 0, 0, 1, ox, oy];
  return {
    kind: 'gradient',
    type: fill.style,
    gradient: fill.gradient,
    angle: fill.angle,
    scale: fill.scale,
    reverse: fill.reverse,
    align: fill.align,
    offset: fill.offset ? { ...fill.offset } : undefined,
    dither: fill.dither,
    interpolationMethod: fill.interpolationMethod,
    referenceBounds: x1 > x0 && y1 > y0 ? { x: x0, y: y0, width: x1 - x0, height: y1 - y0 } : { ...d },
    documentBounds: { ...d },
    referenceTransform: place,
    documentTransform: [...place],
  };
}

// Twin of Rust `build_vector_stroke`; the TS scene carries no pattern paint (as for the fill), so a tile stroke stays `color`.
// `ox/oy`: the page's document origin; `box`: the box in page px, where a legacy ramp is laid out as the fill's.
function buildStroke(
  stroke: VectorStrokeData | undefined,
  path: readonly ScenePathSeg[],
  ox: number,
  oy: number,
  box: { x: number; y: number; width: number; height: number },
): SceneVectorStroke | undefined {
  if (!stroke || stroke.width <= 0) return undefined;
  const paint = stroke.fill;
  const fill =
    paint?.kind === 'gradient'
      ? shapeGradientFill(paint, path, ox, oy)
      : paint?.kind === 'linearGradient' || paint?.kind === 'radialGradient'
        ? buildFill(paint, box.x, box.y, box.width, box.height)
        : undefined;
  return {
    ...(fill && { fill }),
    color: rawColorToCss(stroke.color),
    width: stroke.width,
    dash: stroke.dashPattern ? [...stroke.dashPattern] : [],
    lineCap: stroke.lineCap,
    lineJoin: stroke.lineJoin,
    ...((stroke.alignment === 'inside' || stroke.alignment === 'outside') && { alignment: stroke.alignment }),
  };
}

export interface SceneClipKnot {
  points: number[];
  linked: boolean;
}

export function bezierPathToClipKnots(path: BezierPath, ox: number, oy: number): SceneClipKnot[] {
  if (path.open || !path.knots || path.knots.length < 2) return [];
  // TS model stores knot points as [Y, X] pairs; Rust expects [X, Y] — swap here.
  return path.knots.map(k => ({
    points: [cpInX(k) + ox, cpInY(k) + oy, anchorX(k) + ox, anchorY(k) + oy, cpOutX(k) + ox, cpOutY(k) + oy],
    linked: false,
  }));
}

// A path vector has no raster to mirror, so flip reflects the geometry about the box centre (box-local,
// before the renderer applies rect.rotation — same order as the image/object src-transform flip).
function mirrorScenePts(pts: number[], flipH: boolean, flipV: boolean, cx: number, cy: number): number[] {
  return pts.map((n, i) => (i % 2 === 0 ? (flipH ? 2 * cx - n : n) : flipV ? 2 * cy - n : n));
}

function mirrorSceneFill(
  fill: SceneVectorFill | undefined,
  flipH: boolean,
  flipV: boolean,
  cx: number,
  cy: number,
): SceneVectorFill | undefined {
  if (!fill || fill.kind === 'solid') return fill;
  const fx = (x: number): number => (flipH ? 2 * cx - x : x);
  const fy = (y: number): number => (flipV ? 2 * cy - y : y);
  if (fill.kind === 'gradient') {
    const mirror = ([a, b, c, d, e, f]: Affine): Affine => [
      flipH ? -a : a,
      flipV ? -b : b,
      flipH ? -c : c,
      flipV ? -d : d,
      fx(e),
      fy(f),
    ];
    return { ...fill, referenceTransform: mirror(fill.referenceTransform), documentTransform: mirror(fill.documentTransform) };
  }

  return fill.kind === 'linearGradient'
    ? { ...fill, x1: fx(fill.x1), y1: fy(fill.y1), x2: fx(fill.x2), y2: fy(fill.y2) }
    : { ...fill, cx: fx(fill.cx), cy: fy(fill.cy) };
}

/** Rust vector drawable (paths + fill + stroke, abs px) from a RenderVectorBox; undefined when nothing to draw. */
export function buildVectorDrawable(box: RenderVectorBox, offsetX: number, offsetY: number): SceneVectorBox | undefined {
  const r = box.rect;
  const ox = r.x + offsetX;
  const oy = r.y + offsetY;
  const path = buildPath(box.vector, ox, oy, r.width, r.height);
  if (path.length === 0) return undefined;
  const vectorFill = box.vector.fill;
  const fill =
    vectorFill?.kind === 'gradient'
      ? shapeGradientFill(vectorFill, path, offsetX, offsetY)
      : buildFill(vectorFill, ox, oy, r.width, r.height);
  const built = buildStroke(box.vector.stroke, path, offsetX, offsetY, { x: ox, y: oy, width: r.width, height: r.height });
  if (!fill && !built) return undefined;
  const flipH = !!box.flipHorizontal;
  const flipV = !!box.flipVertical;
  const cx = ox + r.width / 2;
  const cy = oy + r.height / 2;
  const strokeFill = built?.fill && (flipH || flipV) ? mirrorSceneFill(built.fill, flipH, flipV, cx, cy) : built?.fill;
  const stroke = built && { ...built, ...(strokeFill && { fill: strokeFill }) };
  return {
    type: 'vector',
    id: box.id,
    rect: { x: ox, y: oy, width: r.width, height: r.height, rotation: r.rotation ?? 0 },
    path: flipH || flipV ? path.map(seg => ({ op: seg.op, pts: mirrorScenePts(seg.pts, flipH, flipV, cx, cy) })) : path,
    fill: flipH || flipV ? mirrorSceneFill(fill, flipH, flipV, cx, cy) : fill,
    stroke,
    fillOpacity: box.vector.fillOpacity ?? 1,
    opacity: box.opacity,
    blend: box.blendMode || 'normal',
    evenOdd: box.vector.paths?.[0]?.fillRule === 'even-odd',
    ...(box.vector.maskBaseBoxId && { clipBaseBoxId: box.vector.maskBaseBoxId }),
  };
}

/** Scales a vector drawable (coords/stroke/dash/gradient) by `s` — for the renderScale downscale. */
export function scaleSceneVector(v: SceneVectorBox, s: number): SceneVectorBox {
  return {
    ...v,
    rect: { x: v.rect.x * s, y: v.rect.y * s, width: v.rect.width * s, height: v.rect.height * s, rotation: v.rect.rotation },
    path: v.path.map(seg => ({ op: seg.op, pts: seg.pts.map(n => n * s) })),
    fill: scaleFill(v.fill, s),
    stroke: v.stroke
      ? {
          ...v.stroke,
          width: v.stroke.width * s,
          dash: v.stroke.dash.map(d => d * s),
          fill: scaleFill(v.stroke.fill, s),
        }
      : undefined,
  };
}

export function scaleFill(fill: SceneVectorFill | undefined, s: number): SceneVectorFill | undefined {
  if (!fill) return undefined;
  if (fill.kind === 'linearGradient') {
    return { ...fill, x1: fill.x1 * s, y1: fill.y1 * s, x2: fill.x2 * s, y2: fill.y2 * s };
  }

  if (fill.kind === 'radialGradient') {
    return { ...fill, cx: fill.cx * s, cy: fill.cy * s, r: fill.r * s };
  }

  if (fill.kind === 'gradient') {
    const scale = (m: Affine): Affine => m.map(n => n * s) as Affine;
    return { ...fill, referenceTransform: scale(fill.referenceTransform), documentTransform: scale(fill.documentTransform) };
  }

  return fill;
}

// A text box's fillGradient is built box-local, then re-anchored here once a nested group's offset is known.
export function shiftSceneVectorFill(fill: SceneVectorFill, dx: number, dy: number): SceneVectorFill {
  if (fill.kind === 'linearGradient') {
    return { ...fill, x1: fill.x1 + dx, y1: fill.y1 + dy, x2: fill.x2 + dx, y2: fill.y2 + dy };
  }

  if (fill.kind === 'radialGradient') {
    return { ...fill, cx: fill.cx + dx, cy: fill.cy + dy };
  }

  if (fill.kind === 'gradient') {
    const shift = ([a, b, c, d, e, f]: Affine): Affine => [a, b, c, d, e + dx, f + dy];
    return { ...fill, referenceTransform: shift(fill.referenceTransform), documentTransform: shift(fill.documentTransform) };
  }

  return fill;
}
